import { Routemize_Constants_AddressType } from '@/api/models/Routemize_Constants_AddressType';
import type { Routemize_DTOs_UserAddressDto } from '@/api/models/Routemize_DTOs_UserAddressDto';
import type { UserAddress } from '@/business/users/AddressManager';

type AnyUserAddress = UserAddress | Routemize_DTOs_UserAddressDto;

/** Treat missing isActive as active for legacy records. */
export function isUserAddressActive(address?: AnyUserAddress | null): boolean {
  return address?.isActive !== false;
}

/**
 * Prefer an active address of the given type. Inactive matches are only used when
 * no other active addresses exist on the user.
 */
export function resolveUserAddressByType<T extends AnyUserAddress>(
  addresses: T[] | null | undefined,
  addressType: Routemize_Constants_AddressType
): T | undefined {
  const matches = (addresses ?? []).filter((address) => address.addressType === addressType);
  if (matches.length === 0) return undefined;

  const activeMatch = matches.find(isUserAddressActive);
  if (activeMatch) return activeMatch;

  const hasOtherActiveAddress = (addresses ?? []).some(
    (address) => address.addressType !== addressType && isUserAddressActive(address)
  );
  if (hasOtherActiveAddress) return undefined;

  return matches[0];
}

/**
 * Resolve the primary/default address for routing — first active address, else first in list.
 */
export function resolvePrimaryUserAddress<T extends AnyUserAddress>(
  addresses: T[] | null | undefined
): T | undefined {
  const list = addresses ?? [];
  if (list.length === 0) return undefined;
  return list.find(isUserAddressActive) ?? list[0];
}

/**
 * Resolve the home address used for route start/end. Prefers active HOME, then any active address.
 */
export function resolveHomeUserAddress<T extends AnyUserAddress>(
  addresses: T[] | null | undefined
): T | undefined {
  return resolveUserAddressByType(addresses, Routemize_Constants_AddressType.HOME)
    ?? resolvePrimaryUserAddress(addresses);
}

export function formatUserAddressDisplay(address?: AnyUserAddress | null): string {
  if (!address) return 'Home address not available';

  if (address.fullAddress?.trim()) {
    return address.fullAddress.trim();
  }

  const formatted = [
    address.addressLine1,
    address.city,
    address.state,
    address.zipCode,
  ]
    .filter(Boolean)
    .join(', ');

  return formatted || 'Home address not available';
}
